import type { AcknowledgementSubject, TaskLog, TmsEventStream, WsAppSyncEvent, WsEvent } from "@storyboard/web-client";

/** Tables synced by walking a delta route forward from a persisted cursor. */
export type CursorSyncTable =
  | "accounts"
  | "workspaceMemberships"
  | "feeds"
  | "permissions"
  | "feedGroups"
  | "feedGroupMemberships"
  | "commandAliases"
  | "templates"
  | "feedPreferences"
  | "items"
  | "unreadEvents"
  | "organizerUnreadEvents";

/** Tables whose only read path is a whole-set fetch, or that own their own cursor already. */
export type RefetchSyncTable =
  | "workspaces"
  | "directWorkspaceInvitations"
  | "workflows"
  | "membershipAvailabilities"
  | "tags"
  | "currentRoutes"
  | "feedItems"
  | "assignmentIntervals"
  | TmsEventSyncTable
  | AcknowledgementSyncTable
  | TaskSyncTable;

/** One table per TMS event stream, so a ping naming `route_event` walks only that stream's delta. */
export type TmsEventSyncTable = `tmsEvents:${TmsEventStream}`;

/** The sync table a TMS event stream's pings mark dirty. */
export function tmsEventSyncTable(stream: TmsEventStream): TmsEventSyncTable {
  return `tmsEvents:${stream}`;
}

/** One table per acknowledgement subject, so a ping naming `stop_event` walks only that subject's log. */
export type AcknowledgementSyncTable = `acknowledgements:${AcknowledgementSubject}`;

/** The sync table an acknowledgement subject's pings mark dirty. */
export function acknowledgementSyncTable(subject: AcknowledgementSubject): AcknowledgementSyncTable {
  return `acknowledgements:${subject}`;
}

/** One table per task log, so a ping naming `pin_board` walks only the pins. */
export type TaskSyncTable = `tasks:${TaskLog}`;

/** The sync table a task log's pings mark dirty. */
export function taskSyncTable(log: TaskLog): TaskSyncTable {
  return `tasks:${log}`;
}

export type SyncTable = CursorSyncTable | RefetchSyncTable;

/**
 * What one AppSync payload tells the client to go and fetch.
 *
 * Only identity fields are read off the payload; the rows themselves are never applied. A signal names the table that
 * changed, the earliest `updatedAt` it carried for that table (so a cursor that does not exist yet can start there
 * rather than at the beginning of time), and the items and content whose per-item reads are re-fetched.
 */
export type SyncSignals = {
  tables: Set<SyncTable>;
  /** Earliest `updatedAt` the payload carried per table; seeds a cursor that does not exist yet. */
  seeds: Map<SyncTable, string>;
  /** Items named by `events` rows, whose read receipts are re-fetched. */
  eventItemIds: Set<string>;
  /** Content ids named by artifact rows, whose parent items get their content re-fetched. */
  contentIds: Set<string>;
};

/** Who is reading the payload, so event rows can be judged as "mine" or "an organizer's". */
export type SignalContext = {
  accountId: string;
  /** Whether the account holds the organizer (`member`) role in the active workspace. */
  isOrganizer: (accountId: string) => boolean;
};

/**
 * The event names the unread-event delta routes return, mirrored from `UNREAD_EVENTS` in
 * `models/src/repository/AccountEvent.ts`. A payload event outside this set cannot change what those routes would
 * return, so it never marks them dirty.
 */
export const UNREAD_EVENT_NAMES: ReadonlySet<string> = new Set([
  "Opened Feed",
  "Marked Feed as Read",
  "Read Feed Item",
  "Saw Feed Item",
]);

type PayloadKey = Exclude<
  keyof WsAppSyncEvent,
  | "asyncEchos"
  | "callConfiguration"
  | "pam"
  | "pingRequest"
  | "deviceCommands"
  | "events"
  | "tmsEventStreams"
  | "assignmentIntervals"
  | "acknowledgements"
  | "tasks"
>;

/**
 * Which sync tables a payload key marks dirty. Keys absent here are wire-fed (applied directly by the ingest), judged
 * row by row (`events`), or carry nothing the client stores.
 */
const TABLES_BY_KEY: Partial<Record<PayloadKey, readonly SyncTable[]>> = {
  accounts: ["accounts"],
  workspaceMemberships: ["workspaceMemberships"],
  feeds: ["feeds"],
  permissions: ["permissions"],
  feedGroups: ["feedGroups"],
  feedGroupMemberships: ["feedGroupMemberships"],
  commandAliases: ["commandAliases"],
  templates: ["templates"],
  feedPreferences: ["feedPreferences"],
  // An item changed: walk the item delta, and the item event log that warms every feed.
  items: ["items", "feedItems"],
  workspaces: ["workspaces"],
  directWorkspaceInvitations: ["directWorkspaceInvitations"],
  workflowItems: ["workflows"],
  scheduleTriggers: ["workflows"],
  broadcastActions: ["workflows"],
  broadcastRecipients: ["workflows"],
  membershipAvailabilities: ["membershipAvailabilities"],
  workspaceTags: ["tags"],
  workspaceMembershipTags: ["tags"],
};

/** Artifact keys whose rows name a `contentId`; the parent item's content is re-fetched. */
const CONTENT_KEYS = ["files", "links", "audioEncodings", "transcriptions", "displayArtifacts", "callRecords"] as const;

type Row = { updatedAt?: string | null; contentId?: string | null };

function earlierOf(a: string | undefined, b: string): string {
  return a === undefined || new Date(b).getTime() < new Date(a).getTime() ? b : a;
}

function mark(signals: SyncSignals, table: SyncTable, updatedAt: string | null | undefined): void {
  signals.tables.add(table);
  if (updatedAt) signals.seeds.set(table, earlierOf(signals.seeds.get(table), updatedAt));
}

/**
 * Judges each event row on its own. Receipts are the bulk of a dispatcher's event traffic and every one of them would
 * otherwise re-walk both unread routes: only an event in the unread set can change what those routes return, and
 * only the reader's own events move the per-account route, only an organizer's the organizer route.
 */
function markEvents(signals: SyncSignals, events: readonly WsEvent[], ctx: SignalContext): void {
  for (const event of events) {
    if (event.itemId) signals.eventItemIds.add(event.itemId);
    if (!UNREAD_EVENT_NAMES.has(event.name) || !event.accountId) continue;
    if (event.accountId === ctx.accountId) mark(signals, "unreadEvents", event.updatedAt);
    if (ctx.isOrganizer(event.accountId)) mark(signals, "organizerUnreadEvents", event.updatedAt);
  }
}

/** Reads the identity fields off one AppSync payload and turns them into fetch signals. */
export function extractSyncSignals(event: WsAppSyncEvent, ctx: SignalContext): SyncSignals {
  const signals: SyncSignals = { tables: new Set(), seeds: new Map(), eventItemIds: new Set(), contentIds: new Set() };

  for (const [key, tables] of Object.entries(TABLES_BY_KEY) as [PayloadKey, readonly SyncTable[]][]) {
    const rows = event[key] as readonly Row[] | undefined;
    if (!rows?.length) continue;
    for (const table of tables) {
      for (const row of rows) mark(signals, table, row.updatedAt);
    }
  }

  markEvents(signals, event.events ?? [], ctx);

  // A stream ping carries no rows and no `updatedAt`: the stream's own cursor says where its delta starts.
  for (const { stream } of event.tmsEventStreams ?? []) {
    signals.tables.add(tmsEventSyncTable(stream));
  }
  // An assignment ping likewise, and one delta covers it whichever integration it names: the read is workspace-wide.
  if (event.assignmentIntervals?.length) signals.tables.add("assignmentIntervals");
  // An acknowledgement ping likewise: the subject's own cursor says where its delta starts.
  for (const { subject } of event.acknowledgements ?? []) {
    signals.tables.add(acknowledgementSyncTable(subject));
  }
  // A task ping likewise: the log's own cursor says where its walk resumes.
  for (const { log } of event.tasks ?? []) {
    signals.tables.add(taskSyncTable(log));
  }

  for (const key of CONTENT_KEYS) {
    for (const row of (event[key] ?? []) as readonly Row[]) {
      if (row.contentId) signals.contentIds.add(row.contentId);
    }
  }

  return signals;
}
